import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { createPortal } from 'react-dom';
import * as XLSX from 'xlsx';
// @mui
import { Typography, Container } from '@mui/material';
import config from "../../config";
import { encryptData } from '../../utils/cryptoUtils';
import 'bootstrap/dist/css/bootstrap.css';
import 'bootstrap/dist/js/bootstrap.bundle';
import '../common.scss';

const CHECK_API_PATH = `${config.API_BASE_URL}check_reconciliation`;
const FETCH_API_PATH = `${config.API_BASE_URL}get_reconciliation_list`;
const SEARCH_AGREEMENT_API_PATH = `${config.API_BASE_URL}search_reconciliation_agreements`;
const UPDATE_AGREEMENT_API_PATH = `${config.API_BASE_URL}update_reconciliation_agreement`;

const normalizeKey = (key) => String(key).toLowerCase().replace(/[^a-z0-9]/g, '');

// detail row mapping
const mapRow = (row) => {
    const normalized = {};
    Object.keys(row).forEach((k) => { normalized[normalizeKey(k)] = row[k]; });

    return {
        id: Number(normalized.id) || 0,
        name: normalized.name || normalized.customername || normalized.customer || '',
        agreement_no: normalized.agreementno || normalized.agreementnumber || normalized.agreement || '',
        transaction_id: normalized.transactionid || normalized.transectionid || normalized.txnid || normalized.utr || '',
        transaction_date: normalized.transactiondate || '',
        excel_amount: normalized.excelamount ?? '',
        db_amount: normalized.dbamount ?? '',
        source: normalized.source || '',
        remark: normalized.remark || '',
        txn_reference: normalized.txnreference || normalized.txnreferance || '',
        status: normalized.status || 'Pending',
    };
};

// summary row mapping (ek upload ka card)
const mapUpload = (row) => ({
    upload_no: Number(row.upload_no) || 0,
    batch_id: row.batch_id || '',
    file_name: row.file_name || '',
    uploaded_at: row.uploaded_at || '',
    reconciled_by: row.reconciled_by || '',
    total: Number(row.total) || 0,
    reconciled: Number(row.reconciled) || 0,
    not_reconciled: Number(row.not_reconciled) || 0,
    not_found: Number(row.not_found) || 0,
});


const getStatusClass = (status) => {
    const s = String(status || '').trim().toLowerCase();

    if (s === 'reconciled via api' || s === 'matched' || s === 'success') {
        return 'bg-success text-white';
    }
    if (s.startsWith('reconciled via')) {
        return 'bg-primary text-white';
    }
    if (s === 'not reconciled' || s === 'pending') {
        return 'bg-warning text-dark';
    }
    if (s === 'not found' || s === 'unmatched' || s === 'failed') {
        return 'bg-danger text-white';
    }
    return 'bg-secondary text-white';
};


const pad = (n) => String(n).padStart(2, '0');

// 2026-10-12 04:54:38 -> 12-10-26 04:54:38
const formatDateTime = (val) => {
    if (!val) return '';
    const m = String(val).match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}):(\d{2}))?/);
    if (!m) return String(val);
    const dateStr = `${m[3]}-${m[2]}-${m[1].slice(2)}`;
    return m[4] ? `${dateStr} ${m[4]}:${m[5]}:${m[6]}` : dateStr;
};

// any date -> dd-mm-yy  (2026-10-12 / 2026-10-12T00:00:00 / 12/10/2026 / 12-10-2026)
const formatDate = (val) => {
    if (!val) return '';
    const s = String(val).trim();

    // yyyy-mm-dd (with optional time)
    let m = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
    if (m) return `${pad(m[3])}-${pad(m[2])}-${m[1].slice(2)}`;

    // dd-mm-yyyy or dd/mm/yyyy
    m = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
    if (m) return `${pad(m[1])}-${pad(m[2])}-${m[3].slice(2)}`;

    return s; // unknown format, show as is
};

const toNumOrBlank = (v) => {
    if (v === '' || v === null || v === undefined) return '';
    const n = Number(v);
    return Number.isNaN(n) ? v : n;
};

// Excel file -> rows array (dates ko YYYY-MM-DD bana deta hai)
const readExcelRows = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
        try {
            const workbook = XLSX.read(event.target.result, { type: 'array', cellDates: true });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            const json = XLSX.utils.sheet_to_json(sheet, { defval: '' });
            const cleaned = json.map((row) => {
                const out = {};
                Object.keys(row).forEach((k) => {
                    const v = row[k];
                    out[k] = v instanceof Date
                        ? `${v.getFullYear()}-${pad(v.getMonth() + 1)}-${pad(v.getDate())}`
                        : v;
                });
                return out;
            });
            resolve(cleaned);
        } catch (err) {
            reject(err);
        }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
});

const postEncrypted = async (url, payload) => {
    const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: encryptData(payload), // <-- encrypted body
    });
    return res.json();
};

// ---------- icons for the Map Agreement Number popup ----------
const svgProps = { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true };
const IconLink = () => (<svg {...svgProps} width={22} height={22}><path d="M10 13a5 5 0 0 0 7.07 0l3-3a5 5 0 0 0-7.07-7.07l-1.5 1.5" /><path d="M14 11a5 5 0 0 0-7.07 0l-3 3a5 5 0 0 0 7.07 7.07l1.5-1.5" /></svg>);
// filled info icon
const IconInfo = () => (
    <svg width={22} height={22} viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="11" fill="currentColor" />
        <rect x="11" y="10.5" width="2" height="7" rx="1" fill="#fff" />
        <circle cx="12" cy="7.3" r="1.3" fill="#fff" />
    </svg>
);
const IconSearch = () => (<svg {...svgProps} width={17} height={17}><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>);
const IconChevron = () => (<svg {...svgProps} width={16} height={16}><polyline points="6 9 12 15 18 9" /></svg>);
const IconUser = () => (<svg {...svgProps} width={17} height={17}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>);
// filled database icon
const IconDb = () => (
    <svg width={22} height={22} viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
        <path d="M12 2C7.03 2 3 3.34 3 5s4.03 3 9 3 9-1.34 9-3-4.03-3-9-3z" />
        <path d="M3 7.5v3.5c0 1.66 4.03 3 9 3s9-1.34 9-3V7.5c-1.9 1.45-5.4 2-9 2s-7.1-.55-9-2z" />
        <path d="M3 13.5V17c0 1.66 4.03 3 9 3s9-1.34 9-3v-3.5c-1.9 1.45-5.4 2-9 2s-7.1-.55-9-2z" />
    </svg>
);
// filled save icon
const IconSave = () => (
    <svg width={16} height={16} viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
        <path d="M17 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7l-4-4zm-5 16a3 3 0 1 1 0-6 3 3 0 0 1 0 6zM15 9H5V5h10v4z" />
    </svg>
);

const ValidateTranssectionReconcillation = () => {
    const userInfo = useSelector((state) => state.userInfo);
    const userId = userInfo?.id;

    const [uploads, setUploads] = useState([]);          // upload-wise summary list
    const [openNo, setOpenNo] = useState(null);          // jo upload khula hai
    const [detailCache, setDetailCache] = useState({});  // { upload_no: [rows] }
    const [loadingNo, setLoadingNo] = useState(null);
    const [exportingNo, setExportingNo] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [searchText, setSearchText] = useState('');    // khule hue upload ke andar search
    const [submitting, setSubmitting] = useState(false);

    // ---------- manual agreement mapping ----------
    const [selectedIds, setSelectedIds] = useState([]);   // selected "Not Found" row ids (open upload)
    const [mapOpen, setMapOpen] = useState(false);
    const [agrInput, setAgrInput] = useState('');
    const [agrPicked, setAgrPicked] = useState(null);     // { agreement_no, customer_name }
    const [suggestions, setSuggestions] = useState([]);
    const [suggestLoading, setSuggestLoading] = useState(false);
    const [suggestError, setSuggestError] = useState('');
    const [showSuggest, setShowSuggest] = useState(false);
    const [activeIdx, setActiveIdx] = useState(-1);
    const [saving, setSaving] = useState(false);

    // ---------- upload list (summary) ----------
    const fetchUploads = useCallback(async (showError = true) => {
        try {
            const result = await postEncrypted(FETCH_API_PATH, { mode: 'summary' });
            if (result && Number(result.success) === 1) {
                const list = Array.isArray(result.data) ? result.data : [];
                setUploads(list.map(mapUpload));
                return true;
            }
            if (showError) toast.error((result && result.message) || "Unable to fetch records.", { position: "top-right" });
            return false;
        } catch (error) {
            console.error("Fetch uploads error:", error);
            if (showError) toast.error("Unable to fetch records.", { position: "top-right" });
            return false;
        }
    }, []);

    // ---------- ek upload ki poori list (detail) -> rows ya null ----------
    const fetchDetail = useCallback(async (uploadNo, showLoader = true) => {
        try {
            if (showLoader) setLoadingNo(uploadNo);
            const result = await postEncrypted(FETCH_API_PATH, {
                mode: 'detail',
                upload_no: uploadNo,
                start: '0',
                length: config.MAX_RECORDS,
            });
            if (result && Number(result.success) === 1) {
                const list = Array.isArray(result.data) ? result.data : [];
                const mapped = list.map(mapRow);
                setDetailCache((prev) => ({ ...prev, [uploadNo]: mapped }));
                return mapped;
            }
            toast.error((result && result.message) || "Unable to fetch records.", { position: "top-right" });
            return null;
        } catch (error) {
            console.error("Fetch detail error:", error);
            toast.error("Unable to fetch records.", { position: "top-right" });
            return null;
        } finally {
            if (showLoader) setLoadingNo(null);
        }
    }, []);

    // page open hote hi uploads list (silent)
    useEffect(() => {
        fetchUploads(false);
    }, [fetchUploads]);

    // agreement autocomplete (debounced)
    useEffect(() => {
        const term = agrInput.trim();
        if (!mapOpen || agrPicked || term.length < 2) {
            setSuggestions([]);
            setSuggestLoading(false);
            setSuggestError('');
            return undefined;
        }
        let cancelled = false;
        const timer = setTimeout(async () => {
            setSuggestLoading(true);
            setSuggestError('');
            try {
                const result = await postEncrypted(SEARCH_AGREEMENT_API_PATH, { search: term, user_id: userId });
                if (cancelled) return;
                if (result && Number(result.success) === 1) {
                    setSuggestions(Array.isArray(result.data) ? result.data : []);
                    setActiveIdx(-1);
                } else {
                    setSuggestions([]);
                    setSuggestError((result && result.message) || "Unable to fetch suggestions.");
                }
            } catch (error) {
                console.error("Agreement search error:", error);
                if (!cancelled) {
                    setSuggestions([]);
                    setSuggestError("Unable to fetch suggestions.");
                }
            } finally {
                if (!cancelled) setSuggestLoading(false);
            }
        }, 300);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [agrInput, agrPicked, mapOpen, userId]);

    // upload par click: khol / band
    const toggleUpload = async (uploadNo) => {
        setSelectedIds([]);
        if (openNo === uploadNo) {
            setOpenNo(null);
            return;
        }
        setOpenNo(uploadNo);
        setSearchText('');
        if (!detailCache[uploadNo]) {
            await fetchDetail(uploadNo);
        }
    };

    // ---------- Export Excel (ek upload ki poori list) ----------
    const handleExport = async (e, u) => {
        e.stopPropagation(); // accordion toggle na ho
        try {
            setExportingNo(u.upload_no);

            let data = detailCache[u.upload_no];
            if (!data) {
                data = await fetchDetail(u.upload_no, false);
            }
            if (!data || data.length === 0) {
                toast.error("No records to export.", { position: "top-right" });
                return;
            }

            const sheetData = data.map((r, i) => ({
                '#': i + 1,
                'Name': r.name,
                'Agreement No': r.agreement_no,
                'Transaction Details': r.transaction_id,
                'Transaction Date': r.transaction_date,
                'Excel Amount': toNumOrBlank(r.excel_amount),
                'DB Amount': toNumOrBlank(r.db_amount),
                'Source': r.source,
                'Status': r.status,
                'Remark': r.remark,
                'Txn Reference': r.txn_reference,
            }));

            const ws = XLSX.utils.json_to_sheet(sheetData);
            ws['!cols'] = [
                { wch: 6 }, { wch: 26 }, { wch: 18 }, { wch: 60 }, { wch: 16 },
                { wch: 14 }, { wch: 14 }, { wch: 12 }, { wch: 16 }, { wch: 34 }, { wch: 30 },
            ];
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, `Upload ${u.upload_no}`);

            const d = new Date();
            const stamp = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
            XLSX.writeFile(wb, `Reconciliation_Upload_${u.upload_no}_${stamp}.xlsx`);
        } catch (error) {
            console.error("Export error:", error);
            toast.error("Unable to export file.", { position: "top-right" });
        } finally {
            setExportingNo(null);
        }
    };

    // ---------- Excel select ----------
    const handleExcelSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const ext = file.name.split('.').pop().toLowerCase();
        if (!['xlsx', 'xls', 'csv'].includes(ext)) {
            toast.error("Please upload only xlsx, xls or csv file.", { position: "top-right" });
            e.target.value = '';
            setSelectedFile(null);
            return;
        }
        setSelectedFile(file);
    };

    const clearFile = () => {
        setSelectedFile(null);
        const input = document.getElementById('excel_upload');
        if (input) input.value = '';
    };

    // ---------- Check Reconciliation ----------
    const handleCheckReconciliation = async () => {
        if (!selectedFile) {
            toast.error("Please upload an excel file first.", { position: "top-right" });
            return;
        }

        try {
            setSubmitting(true);
            const excelRows = await readExcelRows(selectedFile);

            if (excelRows.length === 0) {
                toast.error("Excel file is empty.", { position: "top-right" });
                setSubmitting(false);
                return;
            }

            const checkResult = await postEncrypted(CHECK_API_PATH, {
                rows: excelRows,
                name: selectedFile.name,
                user_id: userId,
            });

            if (checkResult && Number(checkResult.success) === 1) {
                toast.success(checkResult.message || "Reconciliation checked successfully.", { position: "top-right" });

                // list refresh, aur naya upload apne aap khul jaye
                await fetchUploads();
                const newNo = checkResult.data && Number(checkResult.data.upload_no);
                if (newNo) {
                    setSelectedIds([]);
                    setOpenNo(newNo);
                    setSearchText('');
                    await fetchDetail(newNo);
                }

                clearFile();
            } else {
                toast.error((checkResult && checkResult.message) || "Something went wrong.", { position: "top-right" });
            }
        } catch (error) {
            console.error("Check reconciliation error:", error);
            toast.error("Unable to connect to server.", { position: "top-right" });
        } finally {
            setSubmitting(false);
        }
    };

    // ---------- khule hue upload ke andar search ----------
    const openRows = useMemo(() => {
        if (openNo === null) return [];
        const all = detailCache[openNo] || [];
        const q = searchText.trim().toLowerCase();
        if (!q) return all;
        return all.filter((r) =>
            [r.name, r.agreement_no, r.transaction_id, r.transaction_date, r.excel_amount, r.db_amount, r.source, r.status, r.remark, r.txn_reference]
                .some((v) => String(v).toLowerCase().includes(q))
        );
    }, [openNo, detailCache, searchText]);

    // ---------- manual agreement mapping (checkbox sirf "Not Found" rows par, select-all nahi) ----------
    const isNotFound = (r) => String(r.status).trim().toLowerCase() === 'not found' && r.id > 0;

    const toggleRowSelect = (id) => {
        setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    };

    const openMapModal = () => {
        setAgrInput('');
        setAgrPicked(null);
        setSuggestions([]);
        setSuggestError('');
        setShowSuggest(false);
        setActiveIdx(-1);
        setMapOpen(true);
    };

    const closeMapModal = () => {
        if (saving) return;
        setMapOpen(false);
        setAgrInput('');
        setAgrPicked(null);
        setSuggestions([]);
        setShowSuggest(false);
    };

    const pickSuggestion = (s) => {
        setAgrPicked({ agreement_no: s.agreement_no, customer_name: s.customer_name || '' });
        setAgrInput(s.agreement_no);
        setShowSuggest(false);
        setSuggestions([]);
        setActiveIdx(-1);
    };

    const handleAgrInputChange = (e) => {
        setAgrInput(e.target.value);
        setAgrPicked(null);
        setShowSuggest(true);
    };

    const handleAgrKeyDown = (e) => {
        if (e.key === 'ArrowDown' && suggestions.length > 0) {
            e.preventDefault();
            setShowSuggest(true);
            setActiveIdx((i) => (i + 1) % suggestions.length);
        } else if (e.key === 'ArrowUp' && suggestions.length > 0) {
            e.preventDefault();
            setActiveIdx((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
        } else if (e.key === 'Enter' && showSuggest && activeIdx >= 0 && suggestions[activeIdx]) {
            e.preventDefault();
            pickSuggestion(suggestions[activeIdx]);
        } else if (e.key === 'Escape') {
            setShowSuggest(false);
        }
    };

    const handleSaveMapping = async () => {
        if (saving) return;
        if (selectedIds.length === 0 || openNo === null) {
            toast.error("Please select at least one Not Found record.", { position: "top-right" });
            return;
        }
        if (!agrPicked) {
            toast.error("Please select a valid Agreement Number from the suggestions.", { position: "top-right" });
            return;
        }

        const uploadNo = openNo;
        try {
            setSaving(true);
            const result = await postEncrypted(UPDATE_AGREEMENT_API_PATH, {
                user_id: userId,
                upload_no: uploadNo,
                ids: selectedIds,
                agreement_no: agrPicked.agreement_no,
            });

            if (result && Number(result.success) === 1) {
                toast.success(result.message || "Agreement mapped successfully.", { position: "top-right" });
                setMapOpen(false);
                setAgrInput('');
                setAgrPicked(null);
                setSuggestions([]);
                setSelectedIds([]);
                await fetchDetail(uploadNo, false);
                await fetchUploads(false);
            } else {
                toast.error((result && result.message) || "Unable to map agreement number.", { position: "top-right" });
            }
        } catch (error) {
            console.error("Map agreement error:", error);
            toast.error("Unable to connect to server.", { position: "top-right" });
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <Helmet>
                <title> Validate Transaction Reconciliation | Ckers </title>
            </Helmet>

            <Container>
                <Typography variant="h4" sx={{ mb: 3 }}> Validate Transaction Reconciliation </Typography>

                <div id="main-wrap" className="mt-2 mb-5">
                    {/* TOP BAR */}
                    <div className="px-3 py-3 border rounded mb-4">
                        <div className="row align-items-end">
                            <div className="col-md-5 mb-md-0 mb-3">
                                <div className="form-group">
                                    <label htmlFor="excel_upload" className="w-100">
                                        <span>Upload Excel</span>
                                        <input
                                            type="file"
                                            id="excel_upload"
                                            name="excel_upload"
                                            className="form-control"
                                            accept=".xlsx,.xls,.csv"
                                            onChange={handleExcelSelect}
                                            disabled={submitting}
                                        />
                                    </label>
                                </div>
                            </div>

                            <div className="col-md-7 text-md-end">
                                {selectedFile && (
                                    <button type="button" className="btn btn-outline-secondary me-2" onClick={clearFile} disabled={submitting}>
                                        Clear
                                    </button>
                                )}
                                <button type="button" className="btn btn-primary" onClick={handleCheckReconciliation} disabled={submitting}>
                                    {submitting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                            Checking...
                                        </>
                                    ) : "Check Reconciliation"}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* UPLOADS LIST */}
                    <div className="px-3 py-3 border rounded">
                        <Typography variant="h6" sx={{ mb: 3 }}>
                            Uploads <span className="badge bg-info rounded px-2 ms-2">{uploads.length}</span>
                        </Typography>

                        {uploads.length === 0 && (
                            <div className="text-center py-4">No records yet. Upload a file and click Check Reconciliation.</div>
                        )}

                        {uploads.map((u) => {
                            const isOpen = openNo === u.upload_no;
                            const isExporting = exportingNo === u.upload_no;
                            return (
                                <div key={u.upload_no} className="border rounded mb-3">
                                    {/* upload header (click karo) */}
                                    <div
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => toggleUpload(u.upload_no)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') toggleUpload(u.upload_no); }}
                                        className={`d-flex flex-wrap align-items-center justify-content-between px-3 py-3 ${isOpen ? 'bg-light border-bottom' : ''}`}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <div className="d-flex align-items-center">
                                            <span className="me-3 fs-5">{isOpen ? '▼' : '▶'}</span>
                                            <div>
                                                <div className="fw-bold">Upload #{u.upload_no}{u.file_name ? ` — ${u.file_name}` : ''}</div>
                                                <small className="text-muted d-block">{formatDateTime(u.uploaded_at)}</small>
                                                <small className="text-muted d-block">Reconciled by: <strong>{u.reconciled_by || '-'}</strong></small>
                                            </div>
                                        </div>
                                        <div className="mt-md-0 mt-2 d-flex flex-wrap align-items-center">
                                            <span className="badge bg-dark me-2">Total: {u.total}</span>
                                            <span className="badge bg-success me-2">Reconciled: {u.reconciled}</span>
                                            <span className="badge bg-warning text-dark me-2">Not Reconciled: {u.not_reconciled}</span>
                                            <span className="badge bg-danger me-3">Not Found: {u.not_found}</span>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-success"
                                                onClick={(e) => handleExport(e, u)}
                                                onKeyDown={(e) => e.stopPropagation()}
                                                disabled={isExporting}
                                            >
                                                {isExporting ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                                                        Exporting...
                                                    </>
                                                ) : "Export Excel"}
                                            </button>
                                        </div>
                                    </div>

                                    {/* khula hua poora list */}
                                    {isOpen && (
                                        <div className="p-3">
                                            <div className="row align-items-center mb-3">
                                                <div className="col-md-6">
                                                    <strong>Records</strong>
                                                    <span className="badge bg-info rounded px-2 ms-2">{openRows.length}</span>
                                                    {selectedIds.length > 0 && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-primary ms-3"
                                                            onClick={openMapModal}
                                                        >
                                                            Map Agreement Number ({selectedIds.length})
                                                        </button>
                                                    )}
                                                </div>
                                                <div className="col-md-6 mt-md-0 mt-2">
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="Search name, agreement no, transaction id..."
                                                        value={searchText}
                                                        onChange={(e) => setSearchText(e.target.value)}
                                                    />
                                                </div>
                                            </div>

                                            <div className="table-responsive" style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '70vh', border: '1px solid #dee2e6', borderRadius: 6 }}>
                                                <table className="table table-bordered table-hover table-striped align-middle mb-0" style={{ minWidth: 1970, fontSize: 14 }}>
                                                    <colgroup>
                                                        <col style={{ width: 50 }} />
                                                        <col style={{ width: 60 }} />
                                                        <col style={{ width: 200 }} />
                                                        <col style={{ width: 150 }} />
                                                        <col style={{ width: 420 }} />
                                                        <col style={{ width: 130 }} />
                                                        <col style={{ width: 120 }} />
                                                        <col style={{ width: 120 }} />
                                                        <col style={{ width: 110 }} />
                                                        <col style={{ width: 140 }} />
                                                        <col style={{ width: 260 }} />
                                                        <col style={{ width: 220 }} />
                                                    </colgroup>
                                                    <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 1, whiteSpace: 'nowrap' }}>
                                                        <tr>
                                                            {/* select-all removed: only per-row checkboxes on Not Found rows */}
                                                            <th />
                                                            <th style={{ width: 70 }}>#</th>
                                                            <th>Name</th>
                                                            <th>Agreement No</th>
                                                            <th>Transaction Details</th>
                                                            <th>Transaction Date</th>
                                                            <th>Bank Amount</th>
                                                            <th>DB Amount</th>
                                                            <th>Source</th>
                                                            <th>Status</th>
                                                            <th>Remark</th>
                                                            <th>Txn Reference</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {loadingNo === u.upload_no ? (
                                                            <tr>
                                                                <td colSpan={12} className="text-center py-4">
                                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                                                    Loading...
                                                                </td>
                                                            </tr>
                                                        ) : openRows.length > 0 ? (
                                                            openRows.map((row, index) => (
                                                                <tr key={`${row.transaction_id}-${index}`}>
                                                                    <td>
                                                                        {isNotFound(row) && (
                                                                            <input
                                                                                type="checkbox"
                                                                                className="form-check-input"
                                                                                checked={selectedIds.includes(row.id)}
                                                                                onChange={() => toggleRowSelect(row.id)}
                                                                            />
                                                                        )}
                                                                    </td>
                                                                    <td>{index + 1}</td>
                                                                    <td className="fw-semibold" style={{ whiteSpace: 'nowrap' }}>{row.name}</td>
                                                                    <td>{row.agreement_no}</td>
                                                                    <td style={{ wordBreak: 'break-word', fontSize: 13 }}>{row.transaction_id}</td>
                                                                    <td style={{ whiteSpace: 'nowrap' }}>{formatDate(row.transaction_date)}</td>
                                                                    <td className="text-end" style={{ whiteSpace: 'nowrap' }}>{row.excel_amount}</td>
                                                                    <td className="text-end" style={{ whiteSpace: 'nowrap' }}>{row.db_amount}</td>
                                                                    <td>{row.source}</td>
                                                                    <td>
                                                                        <span className={`badge ${getStatusClass(row.status)}`}>{row.status}</span>
                                                                    </td>
                                                                    <td style={{ whiteSpace: 'nowrap' }}>{row.remark}</td>
                                                                    <td style={{ wordBreak: 'break-word', fontSize: 13 }}>{row.txn_reference}</td>
                                                                </tr>
                                                            ))
                                                        ) : (
                                                            <tr>
                                                                <td colSpan={12} className="text-center py-4">No matching records found.</td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </Container>

            {/* MAP AGREEMENT NUMBER MODAL (compact) */}
            {mapOpen && createPortal(
                <>
                    <div className="modal fade show d-block" tabIndex={-1} role="dialog" aria-modal="true" style={{ overflowY: 'auto', zIndex: 2000 }}>
                        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 640 }}>
                            <div className="modal-content border-0 shadow" style={{ borderRadius: 8, width: '100%' }}>
                                <div className="modal-body" style={{ padding: '22px 24px', fontSize: 14 }}>
                                    {/* header */}
                                    <div className="d-flex align-items-start mb-3">
                                        <div
                                            className="d-flex align-items-center justify-content-center flex-shrink-0 me-3 text-primary"
                                            style={{ width: 44, height: 44, borderRadius: 8, background: '#e8edff' }}
                                        >
                                            <IconLink />
                                        </div>
                                        <div className="flex-grow-1 border-bottom pb-2">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <h5 className="modal-title fw-bold mb-0" style={{ fontSize: 18, lineHeight: '38px' }}>Map Agreement Number</h5>
                                                <button type="button" className="btn-close" style={{ fontSize: 12 }} aria-label="Close" onClick={closeMapModal} disabled={saving} />
                                            </div>
                                        </div>
                                    </div>

                                    {/* info box */}
                                    <div className="d-flex align-items-center rounded mb-3 text-dark" style={{ background: '#eef2ff', padding: '12px 16px', fontSize: 14, lineHeight: 1.5 }}>
                                        <span className="text-primary me-3 d-flex flex-shrink-0"><IconInfo /></span>
                                        <div>
                                            The selected Not Found records will be mapped to the chosen agreement.
                                            <br />
                                            Only the agreement number and customer name are updated;
                                            <br />
                                            the status and remark stay unchanged.
                                        </div>
                                    </div>

                                    {/* agreement number */}
                                    <label htmlFor="map_agreement_no" className="form-label fw-bold mb-1" style={{ fontSize: 14 }}>Agreement Number</label>
                                    <div className="position-relative mb-3">
                                        <span className="position-absolute top-50 translate-middle-y text-primary d-flex" style={{ left: 12, pointerEvents: 'none' }}>
                                            <IconSearch />
                                        </span>
                                        <input
                                            type="text"
                                            id="map_agreement_no"
                                            className="form-control"
                                            style={{ height: 38, paddingLeft: 40, paddingRight: 36, fontSize: 14 }}
                                            placeholder="Type to search agreement number..."
                                            autoComplete="off"
                                            value={agrInput}
                                            onChange={handleAgrInputChange}
                                            onFocus={() => setShowSuggest(true)}
                                            onKeyDown={handleAgrKeyDown}
                                            disabled={saving}
                                        />
                                        <span className="position-absolute top-50 translate-middle-y text-primary d-flex" style={{ right: 12, pointerEvents: 'none' }}>
                                            <IconChevron />
                                        </span>

                                        {showSuggest && !agrPicked && agrInput.trim().length >= 2 && (
                                            <div
                                                className="list-group position-absolute w-100 shadow-sm"
                                                style={{ top: '100%', zIndex: 5, maxHeight: 200, overflowY: 'auto', fontSize: 14 }}
                                            >
                                                {suggestLoading && (
                                                    <div className="list-group-item text-muted py-2">
                                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                                        Searching...
                                                    </div>
                                                )}
                                                {!suggestLoading && suggestError && (
                                                    <div className="list-group-item text-danger py-2">{suggestError}</div>
                                                )}
                                                {!suggestLoading && !suggestError && suggestions.length === 0 && (
                                                    <div className="list-group-item text-muted py-2">No matching agreement found.</div>
                                                )}
                                                {!suggestLoading && suggestions.map((s, i) => (
                                                    <button
                                                        type="button"
                                                        key={s.agreement_no}
                                                        className={`list-group-item list-group-item-action py-2 ${i === activeIdx ? 'active' : ''}`}
                                                        onMouseDown={(e) => e.preventDefault()}
                                                        onClick={() => pickSuggestion(s)}
                                                    >
                                                        <div className="fw-semibold">{s.agreement_no}</div>
                                                        <small>{s.customer_name}</small>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* customer name */}
                                    <label htmlFor="map_customer_name" className="form-label fw-bold mb-1" style={{ fontSize: 14 }}>Customer Name</label>
                                    <div className="position-relative mb-3">
                                        <span className="position-absolute top-50 translate-middle-y text-secondary d-flex" style={{ left: 12, pointerEvents: 'none' }}>
                                            <IconUser />
                                        </span>
                                        <input
                                            type="text"
                                            id="map_customer_name"
                                            className="form-control"
                                            style={{ height: 38, paddingLeft: 40, background: '#f5f6fa', fontSize: 14 }}
                                            value={agrPicked ? agrPicked.customer_name : ''}
                                            placeholder="Auto-filled on selecting an agreement"
                                            readOnly
                                        />
                                    </div>

                                    {/* records count */}
                                    <div className="d-flex align-items-center border-bottom pb-3 mb-3">
                                        <span className="text-primary me-2 d-flex"><IconDb /></span>
                                        <span className="fw-semibold me-2">Records to update:</span>
                                        <span className="badge bg-primary rounded px-2" style={{ fontSize: 13, paddingTop: 6, paddingBottom: 6 }}>{selectedIds.length}</span>
                                    </div>

                                    {/* footer buttons */}
                                    <div className="d-flex justify-content-end">
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary fw-semibold me-2"
                                            style={{ fontSize: 13, padding: '7px 22px', background: '#f5f6fa' }}
                                            onClick={closeMapModal}
                                            disabled={saving}
                                        >
                                            CANCEL
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-primary fw-semibold d-flex align-items-center"
                                            style={{ fontSize: 13, padding: '7px 20px' }}
                                            onClick={handleSaveMapping}
                                            disabled={saving || !agrPicked || selectedIds.length === 0}
                                        >
                                            {saving ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                                    SAVING...
                                                </>
                                            ) : (
                                                <>
                                                    <span className="me-2 d-flex"><IconSave /></span>
                                                    SAVE MAPPING
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-backdrop fade show" style={{ zIndex: 1999 }} />
                </>,
                document.body
            )}
        </>
    );
};

export default ValidateTranssectionReconcillation;